import {
  BarChartOutlined,
  SafetyCertificateOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import { Typography } from 'antd';
import { Outlet, useLocation } from 'react-router-dom';
import { APP_NAME, LOGO_PATH } from '@/constants/brand';
import { getRouteMeta } from '@/constants/routes';
import { useResolvedTheme } from '@/hooks/useResolvedTheme';
import styles from './AuthLayout.module.scss';

const BRAND_FEATURES = [
  { icon: ShoppingOutlined, label: 'Products & inventory' },
  { icon: BarChartOutlined, label: 'Sales & analytics' },
  { icon: SafetyCertificateOutlined, label: 'Secure admin access' },
] as const;

export function AuthLayout() {
  const location = useLocation();
  const resolvedTheme = useResolvedTheme();
  const meta = getRouteMeta(location.pathname);

  return (
    <div className={styles.wrapper} data-theme={resolvedTheme}>
      <aside className={styles.brandPanel}>
        <div className={styles.brandMesh} aria-hidden="true" />
        <div className={styles.brandDecor} aria-hidden="true">
          <span className={styles.decorRing} />
          <span className={styles.decorRing} />
          <span className={styles.decorEmblem} />
          <span className={styles.decorOrb} />
        </div>

        <div className={styles.brandContent}>
          <div className={styles.brandLogoStrip}>
            <img src={LOGO_PATH} alt={APP_NAME} className={styles.brandLogo} />
          </div>

          <Typography.Paragraph className={styles.brandTagline}>
            Publications commerce admin — manage catalog, orders, and insights in one place.
          </Typography.Paragraph>

          <ul className={styles.featureList}>
            {BRAND_FEATURES.map(({ icon: Icon, label }) => (
              <li key={label} className={styles.featureItem}>
                <span className={styles.featureIcon}>
                  <Icon />
                </span>
                <span>{label}</span>
              </li>
            ))}
          </ul>
        </div>

        <svg
          className={styles.brandWave}
          viewBox="0 0 48 800"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M0,0 L48,0 L48,800 L0,720 C16,640 32,560 24,480 C16,400 0,320 8,240 C16,160 32,80 0,0 Z"
            fill="currentColor"
          />
        </svg>
      </aside>

      <main className={styles.formPanel}>
        <div className={styles.formAmbient} aria-hidden="true">
          <span className={styles.formOrb} />
          <span className={styles.formOrb} />
          <span className={styles.formGrid} />
        </div>

        <div className={styles.formInner}>
          <div className={styles.mobileBrand}>
            <div className={styles.mobileLogoStrip}>
              <img src={LOGO_PATH} alt={APP_NAME} className={styles.mobileLogo} />
            </div>
          </div>

          <div className={styles.formCard}>
            <div className={styles.formHeader}>
              <span className={styles.formBadge}>Admin Portal</span>
              <Typography.Title level={3} className={styles.title}>
                {meta?.heading ?? 'Welcome back'}
              </Typography.Title>
              <Typography.Text className={styles.subtitle}>
                {meta?.subtitle ?? 'Sign in to your admin account'}
              </Typography.Text>
            </div>

            <div className={styles.formBody} key={location.pathname}>
              <Outlet />
            </div>
          </div>

          <footer className={styles.pageFooter}>
            &copy; {new Date().getFullYear()} {APP_NAME}. All rights reserved.
          </footer>
        </div>
      </main>
    </div>
  );
}
